import { IMachineState } from '../interfaces/i-machine-state';
import { ITrajectory } from '../interfaces/pitches';
import { ISpin, IVelocity } from '../interfaces/pitches/i-base';
import { IRapsodoShot } from '../interfaces/training/i-rapsodo-shot';
import { BallHelper, BASEBALL_DRAG_COEFF } from './ball.helper';
import { BreakHelper } from './break.helper';
import {
  DEG_RIGHT_ANGLE,
  DEG_TO_RAD,
  METERS_TO_FT,
  METERS_TO_INCHES,
  MPH_TO_FTPS,
  MPS_TO_MPH,
} from './math.utilities';
import { PLATE_EDGE_FT } from './plate.helper';

export class RapsodoHelper {
  private static _degreesToSpin = (value: number): ISpin => {
    const valueRAD = value * DEG_TO_RAD;

    const output: ISpin = {
      wx: -Math.sin(valueRAD),
      wy: 0,
      wz: Math.cos(valueRAD),
    };

    return output;
  };

  static get3DSpin = (data: IRapsodoShot): ISpin | undefined => {
    if (!data.PITCH_SpinAxis || !data.PITCH_TotalSpin) {
      return undefined;
    }

    const spinDegrees = BallHelper.convertTimeToDegrees(data.PITCH_SpinAxis);
    if (spinDegrees === undefined) {
      return undefined;
    }

    // the spin axis is orthogonal to the direction of the hour hand
    const spinAxis = this._degreesToSpin(spinDegrees + DEG_RIGHT_ANGLE);
    const gyroRAD = data.PITCH_GyroDegree * DEG_TO_RAD;

    const output: ISpin = {
      wx: Math.cos(gyroRAD) * data.PITCH_TotalSpin * spinAxis.wx,
      wy: -Math.sin(gyroRAD) * data.PITCH_TotalSpin,
      wz: Math.cos(gyroRAD) * data.PITCH_TotalSpin * spinAxis.wz,
    };

    return output;
  };

  static getGlobal3DSpinRPM = (
    ms: IMachineState,
    data: IRapsodoShot
  ): ISpin | undefined => {
    const givenLocalSpin = this.get3DSpin(data);
    if (!givenLocalSpin) {
      return undefined;
    }
    const velocity = this.get3DSpeedMPH(ms, data);
    const output = BallHelper.getGlobalSpin(givenLocalSpin, velocity);

    return output;
  };

  static get3DSpeedMPH = (ms: IMachineState, data: IRapsodoShot): IVelocity => {
    // moved from RapsodoProcessor
    const speedMPH = data.PITCH_Speed * MPS_TO_MPH;
    const horRAD = (data.PITCH_HorizontalAngle ?? -ms.yaw) * DEG_TO_RAD;
    const vertRAD = (data.PITCH_VerticalAngle ?? ms.tilt) * DEG_TO_RAD;

    // TODO: Probably need to ask rapsodo which approach is correct:
    const METHOD_1 = true;

    if (METHOD_1) {
      // approach 1 MAE: [4.14, 1.74, 1.17]
      const output: IVelocity = {
        vx: -speedMPH * Math.sin(horRAD) * Math.cos(vertRAD),
        vy: -speedMPH * Math.cos(horRAD) * Math.cos(vertRAD),
        vz: speedMPH * Math.sin(vertRAD),
      };
      return output;
    }

    // approach 2 MAE: [4.15, 1.74, 1.16]
    const output: IVelocity = {
      vx: -speedMPH * Math.sin(horRAD),
      vy: -speedMPH * Math.cos(horRAD) * Math.cos(vertRAD),
      vz: speedMPH * Math.cos(horRAD) * Math.sin(vertRAD),
    };
    return output;
  };

  static getTrajectory(ms: IMachineState, data: IRapsodoShot): ITrajectory {
    const _getApproximateTF = (data: {
      dy_ft: number;
      vy_ftps: number;
      ay_ftpsps: number;
    }): number => {
      return (
        (-data.vy_ftps -
          Math.sqrt(
            data.vy_ftps * data.vy_ftps - 2 * data.ay_ftpsps * data.dy_ft
          )) /
        data.ay_ftpsps
      );
    };

    const veloMPH = RapsodoHelper.get3DSpeedMPH(ms, data);

    const ay = BASEBALL_DRAG_COEFF * Math.pow(veloMPH.vy * MPH_TO_FTPS, 2);

    // time of flight, in seconds
    const time =
      data.PITCH_ZoneTime === undefined
        ? _getApproximateTF({
            dy_ft: ms.py - PLATE_EDGE_FT,
            vy_ftps: veloMPH.vy * MPH_TO_FTPS,
            ay_ftpsps: ay,
          })
        : data.PITCH_ZoneTime;

    const vna = BreakHelper.getVeloAccel({
      initial: {
        pos: {
          px: ms.px,
          py: ms.py,
          pz: ms.pz,
        },
        velo: {
          vx: veloMPH.vx * MPH_TO_FTPS,
          vy: veloMPH.vy * MPH_TO_FTPS,
          vz: veloMPH.vz * MPH_TO_FTPS,
        },
      },
      final: {
        pos: {
          px: -data.PITCH_StrikeZoneSide * METERS_TO_FT,
          py: PLATE_EDGE_FT,
          pz: data.PITCH_StrikeZoneHeight * METERS_TO_FT,
        },
        time: time,
      },
      breaks: {
        xInches: -data.PITCH_HBTrajectory * METERS_TO_INCHES,
        zInches: data.PITCH_VBTrajectory * METERS_TO_INCHES,
      },
    });

    const output: ITrajectory = {
      px: ms.px,
      py: ms.py,
      pz: ms.pz,

      vx: vna.velo.vx,
      vy: vna.velo.vy,
      vz: vna.velo.vz,

      ax: vna.accel.ax,
      ay: vna.accel.ay,
      az: vna.accel.az,

      wnet: data.PITCH_TotalSpin,
    };

    return output;
  }
}
